import { SagaIterator } from 'redux-saga';
import { all, takeLatest, call, put } from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import Services from 'services';
import {
  fetchUserFailure,
  fetchUserSuccess,
  updateUserFailure,
  updateUserSuccess,
  setAuthenticated,
  setUserSession,
  fetchUserStart,
  startSession,
  authenticationSuccess,
  authenticationFailure,
  loadCustomBaseMapSuccess,
  loadCustomBaseMapFailure,
  mailboxUnmatchedCountSuccess,
  mailboxUnmatchedCountFailure,
  setMaintenance,
  fetchAlertNotificationStatusSuccess,
  fetchAlertNotificationStatusFailure
} from './actions';
import { getAlertNotificationStatusFromAppConfig } from 'services/app-config.service';
import { SessionActionTypes } from './types';

function hotjarIdentify(user: any) {
  if (typeof window.hj === 'function') {
    window.hj('identify', user.id, {
      'orgID ': user.organisation_id
    });
  }
}

export function* handleFetchAlertNotificationStatus(): SagaIterator {
  try {
    const data: any = yield call(getAlertNotificationStatusFromAppConfig);
    yield put(fetchAlertNotificationStatusSuccess(data));
  } catch (error) {
    yield put(fetchAlertNotificationStatusFailure(error));
  }
}

export function* onFetchAlertNotificationStatus() {
  yield takeLatest(
    SessionActionTypes.FETCH_ALERT_NOTIFICATION_STATUS_START,
    handleFetchAlertNotificationStatus
  );
}

export function* handleFetchUser() {
  try {
    const response: AxiosResponse = yield call(Services.user.getProfile);
    yield call(hotjarIdentify, response?.data);

    if (response?.data) {
      yield put(setUserSession(response.data));
      yield put(fetchUserSuccess());
    } else {
      yield put(fetchUserFailure('Unable to fetch User profile'));
    }
  } catch (error) {
    let msg = 'Unable to fetch User profile';
    let errStatus: number | undefined;

    if (Axios.isAxiosError(error)) {
      msg = error.response?.data?.msg ?? msg;
      errStatus = error.response?.status;
    }

    if (errStatus === 500 || errStatus === undefined) {
      yield put(setMaintenance(true));
    }

    yield put(setUserSession({}));
    yield put(setAuthenticated(false));
    yield put(fetchUserFailure(msg));
  }
}

export function* handleUpdateUser(request: any) {
  try {
    const response: AxiosResponse = yield call(
      Services.user.updateProfile,
      request.payload
    );
    if (response?.data) {
      yield put(setUserSession(response.data));
      yield put(updateUserSuccess());
    } else {
      yield put(updateUserFailure('Unable to update User profile'));
    }
  } catch (error) {
    const msg = Axios.isAxiosError(error)
      ? error.response?.data?.msg
      : undefined;
    yield put(updateUserFailure(msg || 'Unable to update User profile'));
  }
}

export function* handleAuthentication(request: any) {
  try {
    const response: AxiosResponse = yield call(
      Services.auth.authentication,
      request.payload
    );
    if (response) {
      yield put(startSession());
      yield put(authenticationSuccess());
      yield put(fetchUserStart());
    }
  } catch (error) {
    if (Axios.isAxiosError(error)) {
      if (error.response && error.response.status === 409) {
        yield put(authenticationFailure(error.response.data?.msg));
      } else if (
        error.response &&
        (error.response.status === 403 || error.response.status === 402)
      ) {
        yield put(authenticationFailure(error.response.data?.msg));
        window.location.href = `${process.env.REACT_APP_IDENTITY_WEB_URL}/error`;
      } else {
        yield put(authenticationFailure('Unable to authenticate User'));
      }
    } else {
      yield put(authenticationFailure('Unable to authenticate User'));
    }
  }
}

export function* handleMailboxUnmatchedCount() {
  try {
    const response: AxiosResponse = yield call(
      Services.mailbox.getMailboxUnmatchedCount
    );
    if (response?.data) {
      yield put(mailboxUnmatchedCountSuccess(response.data));
    } else {
      yield put(
        mailboxUnmatchedCountFailure('Unable to get mailbox unmatched count')
      );
    }
  } catch (error) {
    const msg = Axios.isAxiosError(error)
      ? error.response?.data?.msg
      : undefined;
    yield put(
      mailboxUnmatchedCountFailure(
        msg || 'Unable to get mailbox unmatched count'
      )
    );
  }
}

export function* handleLoadCustomBaseMap(request: any) {
  try {
    const customBaseMap: __esri.Basemap = yield call(
      Services.map.getCustomBaseMap,
      request.payload.id
    );
    if (customBaseMap) {
      yield put(loadCustomBaseMapSuccess(customBaseMap));
    } else {
      yield put(loadCustomBaseMapFailure('Unable to get custom basemap'));
    }
  } catch (error) {
    yield put(loadCustomBaseMapFailure('Unable to get custom basemap'));
  }
}

export function* onFetchUser() {
  yield takeLatest(SessionActionTypes.FETCH_USER_START, handleFetchUser);
}

export function* onUpdateUser() {
  yield takeLatest(SessionActionTypes.UPDATE_USER_START, handleUpdateUser);
}

export function* onAuthentication() {
  yield takeLatest(
    SessionActionTypes.AUTHENTICATION_START,
    handleAuthentication
  );
}

export function* onMailboxUnmatchedCount() {
  yield takeLatest(
    SessionActionTypes.MAILBOX_UNMATCHED_COUNT_START,
    handleMailboxUnmatchedCount
  );
}

export function* onLoadCustomBaseMap() {
  yield takeLatest(
    SessionActionTypes.LOAD_CUSTOM_BASEMAP_START,
    handleLoadCustomBaseMap
  );
}

export function* userSagas() {
  yield all([
    call(onFetchUser),
    call(onUpdateUser),
    call(onAuthentication),
    call(onLoadCustomBaseMap),
    call(onMailboxUnmatchedCount),
    call(onFetchAlertNotificationStatus)
  ]);
}

export default userSagas;
